import { useRef, useState } from 'react';
import { useNavigate } from "react-router-dom";

// material-ui
import useMediaQuery from '@mui/material/useMediaQuery';
import Avatar from '@mui/material/Avatar';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemAvatar from '@mui/material/ListItemAvatar';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';

// assets
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faServer, faFileLines, faDesktop, faAirFreshener, faChair } from '@fortawesome/free-solid-svg-icons';

import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import GiftOutlined from '@ant-design/icons/GiftOutlined';
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import SettingOutlined from '@ant-design/icons/SettingOutlined';

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',

  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [read, setRead] = useState(2);
  const [open, setOpen] = useState(false);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleClickNotif = () => {
    navigate("/maintenance-reminder")
    setOpen(false)
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={read} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title="Notification"
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {read > 0 && (
                        <Tooltip title="Mark as all read">
                          <IconButton color="success" size="small" onClick={() => setRead(0)}>
                            <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  <List
                    component="nav"
                    sx={{
                      p: 0,
                      '& .MuiListItemButton-root': {
                        py: 0.5,
                        px: 2,
                        '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
                        '& .MuiAvatar-root': avatarSX,
                        '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
                      }
                    }}
                  >
                     <ListItem
                      onClick={handleClickNotif}
                      component={ListItemButton}
                      divider
                      selected={read > 0}
                      secondaryAction={
                        <Typography variant="caption" noWrap>
                          01:15
                        </Typography>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ color: 'error.main', bgcolor: 'error.lighter' }}>
                        <FontAwesomeIcon icon={faFileLines} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">
                           Your contract with {' '}
                            <Typography component="span" variant="subtitle1">
                              PT BAHARI SENTOSA
                            </Typography>{' '}
                           will end soon.
                          </Typography>
                        }
                        secondary="2 hours ago"
                      />
                    </ListItem>
                    <ListItem
                    onClick={handleClickNotif}
                      component={ListItemButton}
                      divider
                      selected={read > 0}
                      secondaryAction={
                        <Typography variant="caption" noWrap>
                          22:45
                        </Typography>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ color: 'error.main', bgcolor: 'error.lighter' }}>
                        <FontAwesomeIcon icon={faFileLines} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">
                           Your contract with {' '}
                            <Typography component="span" variant="subtitle1">
                              PT KRAKATAU STEEL
                            </Typography>{' '}
                           will end soon.
                          </Typography>
                        }
                        secondary="4 hours ago"
                      />
                    </ListItem>
                    <ListItem
                    onClick={handleClickNotif}
                      component={ListItemButton}
                      divider
                      selected={read > 0}
                      secondaryAction={
                        <Typography variant="caption" noWrap>
                           21:40
                        </Typography>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ color: 'error.main', bgcolor: 'error.lighter' }}>
                        <FontAwesomeIcon icon={faAirFreshener} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">
                           Your {' '}
                            <Typography component="span" variant="subtitle1">
                            Air Conditioner
                            </Typography>{' '}
                           need to maintenance soon.
                          </Typography>
                        }
                        secondary="5 hours ago"
                      />
                    </ListItem>
                    <ListItem
                    onClick={handleClickNotif}
                      component={ListItemButton}
                      divider
                      selected={read > 0}
                      secondaryAction={
                        <Typography variant="caption" noWrap>
                          19:40
                        </Typography>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ color: 'error.main', bgcolor: 'error.lighter' }}>
                        <FontAwesomeIcon icon={faDesktop} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">
                           Your {' '}
                            <Typography component="span" variant="subtitle1">
                              Computer
                            </Typography>{' '}
                           need to maintenance soon.
                          </Typography>
                        }
                        secondary="7 hours ago"
                      />
                    </ListItem>
                    <ListItem
                    onClick={handleClickNotif}
                      component={ListItemButton}
                      divider
                      selected={read > 0}
                      secondaryAction={
                        <Typography variant="caption" noWrap>
                         16:15
                        </Typography>
                      }
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ color: 'error.main', bgcolor: 'error.lighter' }}>
                        <FontAwesomeIcon icon={faServer} />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="h6">
                           Your{' '}
                            <Typography component="span" variant="subtitle1">
                              Server
                            </Typography>{' '}
                            need to maintenance soon.
                          </Typography>
                        }
                        secondary="10 hours ago"
                      />
                    </ListItem>
                    <ListItemButton sx={{ textAlign: 'center', py: `${12}px !important` }}>
                      <ListItemText
                        primary={
                          <Typography variant="h6" color="primary">
                            View All
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </List>
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
